import React, { useEffect, useState, useCallback, useRef } from "react";
import { useIntl, FormattedDate } from "react-intl";
import { useHistory } from "react-router-dom";
import { FileText, Plus, ChevronUp, ChevronDown } from "react-feather";
import Skeleton from "../../components/Skeleton";
import Pagination from "../../components/Pagination";
import FeedbackMessage from "../../components/FeedbackMessage";
import LogoViewer from "../../components/LogoViewer";
import Select from '../../components/Select';
import { getOperationsHistory, verifyFeaturePermission } from "../../api";
import { formatCpfCnpj } from "../../utils/format";
import { debouncer } from "../../utils/debouncer";
import "./styles.scss";
import OperationTypePickerModal from "./components/OperationTypePickerModal";
import { MdCached } from "react-icons/md";

const PAGE_SIZE = 20;

const OPERATION_TYPE_PICKER_MODAL = "OPERATION_TYPE_PICKER_MODAL";

const Operations = ({ setError }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const analysisErrorRef = useRef(null);

  const handleRowClick = operation => {
    history.push(`/edit-operation/${operation.id}`);
  };

  const handleCreateOperationClick = useCallback(
    type => {
      history.push(`/new-operation?startFrom=${type}`);
    },
    [history]
  );

  const [searchOptions, setSearchOptions] = useState({
    busca: null,
    orderBy: null
  });

  useEffect(() => {
    setLoading(true);

    getOperationsHistory(page, PAGE_SIZE, searchOptions.busca, searchOptions.orderBy)
      .then(res => {
        setOperations(res.data.result.data);
        setTotalPages(res.data.result.totalPages);
      })
      .catch(() => setError(formatMessage({ id: "erro-api" })))
      .finally(() => {
        setLoading(false);
      });
  },
    [setError, formatMessage, page, searchOptions]
  );

  const filterOperationListing = debouncer((params) => {
    setSearchOptions({ ...searchOptions, ...params });
    setPage(1);
  }, 600);

  const [isLoadingClickHandler, setIsLoadingClickHandler] = useState(false);
  const operationClickHandler = useCallback(() => {
    setIsLoadingClickHandler(true);

    verifyFeaturePermission(["proposta.inicio_gleba"]).then(res => {
      const result = res.data;

      setIsLoadingClickHandler(false);

      if (!result.has_access) {
        handleCreateOperationClick("car");
        return;
      }

      setOpenModal(OPERATION_TYPE_PICKER_MODAL);
    });
  }, [handleCreateOperationClick]);

  const orderOptions = [
    { label: 'Data de modificação - Recente', value: 'B3Proposta.modified DESC' },
    { label: 'Data de modificação - Antigo', value: 'B3Proposta.modified ASC' },
    { label: 'Data de criação - Recente', value: 'B3Proposta.created DESC' },
    { label: 'Data de criação - Antigo', value: 'B3Proposta.created ASC' }
  ];

  return (
    <>
      <div className="operation-header">
        <h1>{formatMessage({ id: "operacao" })}</h1>

        <LogoViewer>
        </LogoViewer>
      </div>
      <div className="operation-header">
        <div className="column is-6">
          <label htmlFor="search-operation" className="search-operation">
            <input
              className="input"
              type="text"
              name="search-operation"
              id="search-operation"
              placeholder="Pesquise por nome, código ou CPF/CNPJ "
              onChange={e => filterOperationListing({ busca: e.target.value })}
            />
          </label>
        </div>

        <div className="operation-header">
          {isLoadingClickHandler && (
            <MdCached className="loader-only-rotation" size={16} />
          )}

          <button
            disabled={isLoadingClickHandler}
            onClick={() => operationClickHandler()}
          >
            <Plus size={20} />
            Criar operação
          </button>
        </div>
      </div>

      <div className="content-box operation">
        {!loading && operations.length === 0 ? (
          <div className="no-results">
            <FileText size="48px" />
            <p>{formatMessage({ id: "nenhuma-operacao-encontrada" })}</p>
          </div>
        ) : (
          <>
            <table className="table is-striped is-hoverable">
              <thead>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="operation-order">
                    <div className="icon-order-container">
                      <div className="icon-order-view">
                        <ChevronUp strokeWidth={5} size={10} />
                        <ChevronDown strokeWidth={5} size={10} />
                      </div>
                      <span>{formatMessage({ id: "ordernar" })}</span>
                      <div className="content-select-button">
                        <Select
                          options={orderOptions}
                          querying
                          onChange={(e) => {
                            filterOperationListing({ orderBy: e.selected.value });
                          }}
                        />{" "}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>{formatMessage({ id: "data-operacao" })}</th>
                  <th>{formatMessage({ id: "nome-operacao" })}</th>
                  <th>{formatMessage({ id: "identificador" })}</th>
                  <th>{formatMessage({ id: "cpf-cnpj" })}</th>
                  <th>{formatMessage({ id: "data-modificacao" })}</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((_, j) => (
                        <td key={j}>
                          <Skeleton />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!loading &&
                  operations.length > 0 &&
                  operations.map(operation => (
                    <tr
                      key={operation.B3Proposta.id}
                      data-testid={`result-${operation.B3Proposta.id}`}
                      onClick={() => handleRowClick(operation.B3Proposta)}
                    >
                      <td>
                        <FormattedDate
                          value={new Date(operation.B3Proposta.created)}
                          year="numeric"
                          month="numeric"
                          day="numeric"
                          hour="numeric"
                          minute="numeric"
                        />
                      </td>
                      <td>
                        {operation.B3Proposta.nome || (
                          <span className="untitled">
                            {formatMessage({ id: "sem-titulo" })}
                          </span>
                        )}
                      </td>
                      <td>
                        {operation.B3Proposta.identificador || (
                          <span className="untitled">
                            {formatMessage({ id: "sem-identificador" })}
                          </span>
                        )}
                      </td>

                      <td>
                        {operation.B3PessoaConsultada &&
                          operation.B3PessoaConsultada.length > 0 &&
                          operation.B3PessoaConsultada.map(pessoa => (
                            <span key={pessoa.id}>
                              {formatCpfCnpj(pessoa.cpf_cnpj)}
                              <br />
                            </span>
                          ))}
                      </td>
                      <td>
                        <FormattedDate
                          value={new Date(operation.B3Proposta.modified)}
                          year="numeric"
                          month="numeric"
                          day="numeric"
                          hour="numeric"
                          minute="numeric"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
          </>
        )}
      </div>

      {analysisError && (
        <FeedbackMessage
          ref={analysisErrorRef}
          level="error"
          message={analysisError}
          onDismiss={() => setAnalysisError(null)}
        />
      )}
      {successMessage && (
        <FeedbackMessage
          style={{
            top: analysisErrorRef.current
              ? `${analysisErrorRef.current.clientHeight + 24}px`
              : undefined
          }}
          level="success"
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {openModal === OPERATION_TYPE_PICKER_MODAL && (
        <OperationTypePickerModal
          onClose={() => setOpenModal(null)}
          onSelectStart={type => handleCreateOperationClick(type)}
        />
      )}
    </>
  );
};

export default Operations;
